import React from "react";
import { Button } from "semantic-ui-react";
import ClientPaymentMethod from "../../Subscription/Detail/ClientPaymentMethod";
import Panel from "../../components/panels/panel";
import StripePaymentIntentRowCard from "../../dashboard/AdminOnePanels/StripePaymentIntent/StripePaymentIntentCard";
import KeyValueSublabelDropdown from "components/dropdowns/reactselect/KeyValueSublabelDropdown";
import { useMutation, useQuery } from "@tanstack/react-query";
import { DetailSectionTemplate } from "components/MainSectionTemplate/DetailSectionTemplate";
import { NoDataAvailableDiv } from "components/NoDataAvailableDiv";
import AdminOneClientCard from "adminone/AdminOneClientsByCompanyPanel/AdminOneClientCard";

interface Props {
  viewModel: server.viewmodel.InvoiceV1DetailFrontModel
}

const AdminOneInvoicePanel = (props: Props) => {
  const [selectedPdfTemplateId, setSelectedPdfTemplateId] = React.useState<number>();
  const [selectedInvoiceEmailTemplateId, setSelectedInvoiceEmailTemplateId] = React.useState<number>();

  const sendInvoiceMutation = useMutation(window.CoreApi.Invoice.SendSubscriptionInvoice);

  const panelDataApi = useQuery({
    queryKey: ["adminOnePanelData", props.viewModel.invoice.invoiceId],
    queryFn: () => window.CoreApi.Invoice.GetAdminOnePanelData(props.viewModel.invoice.invoiceId).then(r => r.data),
    onSuccess: (data) => {
      if (data.hasErrors) {
        return;
      }
      if (data.data.clients.length > 0) {
        setSelectedPdfTemplateId(data.data.clients[0].invoiceWordTemplateId);
        setSelectedInvoiceEmailTemplateId(data.data.clients[0].invoiceEmailTemplateId);
      }
    }
  })

  const sendInvoice = async () => {
    if (sendInvoiceMutation.isLoading) return;

    const apiCall = await sendInvoiceMutation.mutateAsync(props.viewModel.invoice.invoiceId);

    if (apiCall.data.hasErrors) {
      window.alert(apiCall.data.errors.map(e => e.message).join(", "));
    } else {
      window.location.reload();
    }
  };

  const onRetryFinished = async (wasSuccessful: boolean) => {
    if (wasSuccessful) { window.location.reload(); }
  };

  const onValueChanged = async (key: string, value: any) => {
    if (key === "pdfTemplate") {
      setSelectedPdfTemplateId(value.key);
    } else if (key === "emailTemplate") {
      setSelectedInvoiceEmailTemplateId(value.key);
    }
  }

  if (panelDataApi.data?.data?.clients.length === 0) {
    return <Panel
      panelClassName="s1_related-feature-panel margin-top"
      title="AdminOne"
    >
      <NoDataAvailableDiv>No matching client found.</NoDataAvailableDiv>
    </Panel>;
  }

  if (selectedPdfTemplateId == null) {
    return <Panel
      title="AdminOne"
      panelClassName="s1_related-feature-panel margin-top"
      isLoading
    >
    </Panel>;
  }

  const selectedPdfTemplate = props.viewModel.invoiceWordTemplates.find(t => t.key === selectedPdfTemplateId);
  const selectedEmailTemplate = props.viewModel.invoiceEmailTemplates.find(t => t.key === selectedInvoiceEmailTemplateId);
  const panelData = panelDataApi.data;

  return <div style={{ marginTop: "var(--spacing-page)" }}>
    <DetailSectionTemplate
      onValueChanged={onValueChanged}
      isInPageEditable
      sections={[
        {
          key: "payment",
          type: "regular",
          title: "Admin One",
          fields: [
            {
              key: "clientCards",
              hideLabel: true,
              label: "",
              render: () => {
                return <div className="flex-column gap" style={{ border: "1px solid var(--border-active)" }}>
                  {panelData?.data?.clients.map((client) => <AdminOneClientCard
                    key={`client_${client.clientId}`}
                    client={client}
                  />)
                  }
                </div>
              }
            },
            {
              key: "stripePaymentIntent",
              hideLabel: true,
              label: "",
              hidden: !panelData?.data.stripePaymentIntent,
              render: () => <StripePaymentIntentRowCard
                payment={panelData?.data.stripePaymentIntent}
                onRetryFinished={onRetryFinished}
              />
            },
            {
              key: "paymentMethod",
              label: RESX.Payment.PaymentMethod,
              hidden: props.viewModel.invoice.lifeCycle !== "draft",
              render: () => <div>
                <ClientPaymentMethod paymentMethod={panelData?.data.paymentMethod} />
              </div>
            },
            {
              key: "pdfTemplate",
              label: "PDF template",
              render: () => <div>{selectedPdfTemplate.value}</div>,
              hidden: props.viewModel.invoice.lifeCycle !== "draft",
              inPageEdit: {
                render: ({ value, onChange }) => <KeyValueSublabelDropdown
                  value={value}
                  onChange={onChange}
                  options={props.viewModel.invoiceWordTemplates}
                />,
                fullWidth: true,
                type: "confirmOrCancelButtonInline",
                initialValue: selectedPdfTemplate,
                submitChange: async (i) => window.CoreApi.Client.UpdateInvoicingTemplateSetting({ clientId: panelData?.data.clients[0].clientId, invoiceEmailTemplateId: selectedInvoiceEmailTemplateId, invoiceWordTemplateId: i.key })
              }
            },
            {
              key: "emailTemplate",
              label: "Email template",
              render: () => <div>{selectedEmailTemplate.value}</div>,
              hidden: props.viewModel.invoice.lifeCycle !== "draft",
              inPageEdit: {
                render: ({ value, onChange }) => <KeyValueSublabelDropdown
                  value={value}
                  onChange={onChange}
                  options={props.viewModel.invoiceEmailTemplates}
                />,
                fullWidth: true,
                type: "confirmOrCancelButtonInline",
                initialValue: selectedEmailTemplate,
                submitChange: async (i) => window.CoreApi.Client.UpdateInvoicingTemplateSetting({ clientId: panelData?.data.clients[0].clientId, invoiceEmailTemplateId: i.key, invoiceWordTemplateId: selectedPdfTemplate.key })
              }
            },
            {
              key: "save",
              hideLabel: true,
              hidden: props.viewModel.invoice.lifeCycle !== "draft",
              label: "",
              render: () => <Button type="button" color="blue" fluid size="big" onClick={sendInvoice} disabled={sendInvoiceMutation.isLoading}>
                SEND THIS INVOICE
              </Button>
            }
          ]
        }
      ]}
    />
  </div>
};

export default AdminOneInvoicePanel;